<!-- eslint-disable vue/v-slot-style -->
<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useIntegrationHook } from 'dashboard/composables/useIntegrationHook';
import { FormKit } from '@formkit/vue';
import { useBranding } from 'shared/composables/useBranding';

import NextButton from 'dashboard/components-next/button/Button.vue';
import vibevoxLlmApi from 'dashboard/api/vibevoxLlm';
import { VIBEVOX_LANGUAGES } from 'dashboard/constants/vibevoxLanguages';

export default {
  components: {
    FormKit,
    NextButton,
  },
  props: {
    integrationId: {
      type: String,
      required: true,
    },
  },
  emits: ['close'],
  setup(props) {
    const { integration, isHookTypeInbox } = useIntegrationHook(
      props.integrationId
    );
    const { replaceInstallationName } = useBranding();

    return { integration, isHookTypeInbox, replaceInstallationName };
  },
  data() {
    return {
      endPoint: '',
      alertMessage: '',
      values: {},
      // VibeVox AI: модели, доступные по введённому ключу, + индикатор проверки
      aiModels: [],
      aiVerifying: false,
    };
  },
  computed: {
    ...mapGetters({
      uiFlags: 'integrations/getUIFlags',
      dialogFlowEnabledInboxes: 'inboxes/dialogFlowEnabledInboxes',
    }),
    inboxes() {
      return this.dialogFlowEnabledInboxes
        .filter(inbox => {
          if (!this.isIntegrationDialogflow) {
            return true;
          }
          return !this.connectedDialogflowInboxIds.includes(inbox.id);
        })
        .map(inbox => ({ label: inbox.name, value: inbox.id }));
    },

    connectedDialogflowInboxIds() {
      if (!this.isIntegrationDialogflow) {
        return [];
      }
      return this.integration.hooks.map(hook => hook.inbox?.id);
    },
    formItems() {
      return this.integration.settings_form_schema;
    },
    isIntegrationDialogflow() {
      return this.integration.id === 'dialogflow';
    },
    // VibeVox AI: интеграция ИИ (OpenAI-совместимый; ключ Gemini индивидуальный для аккаунта)
    isIntegrationOpenai() {
      return this.integration.id === 'openai';
    },
    // VibeVox: опции языка оператора (108) для аккаунт-дефолта перевода
    langOptions() {
      return VIBEVOX_LANGUAGES.map(l => ({ label: `${l.name} — ${l.en}`, value: l.code }));
    },
  },
  methods: {
    onClose() {
      this.$emit('close');
    },
    buildHookPayload() {
      const hookPayload = {
        app_id: this.integration.id,
        settings: {},
      };

      hookPayload.settings = Object.keys(this.values).reduce((acc, key) => {
        if (key !== 'inbox') {
          acc[key] = this.values[key];
        }
        return acc;
      }, {});

      this.formItems.forEach(item => {
        if (item.validation?.includes('JSON')) {
          hookPayload.settings[item.name] = JSON.parse(
            hookPayload.settings[item.name]
          );
        }
      });

      if (this.isHookTypeInbox && this.values.inbox) {
        hookPayload.inbox_id = this.values.inbox;
      }

      return hookPayload;
    },
    async submitForm() {
      try {
        await this.$store.dispatch(
          'integrations/createHook',
          this.buildHookPayload()
        );
        this.alertMessage = this.$t('INTEGRATION_APPS.ADD.API.SUCCESS_MESSAGE');
        this.onClose();
      } catch (error) {
        const errorMessage = error?.response?.data?.message;
        this.alertMessage =
          errorMessage || this.$t('INTEGRATION_APPS.ADD.API.ERROR_MESSAGE');
      } finally {
        useAlert(this.alertMessage);
      }
    },
    // VibeVox AI: «Проверить» — валидируем ключ и тянем список доступных по нему моделей.
    async verifyAiKey() {
      const apiKey = this.values?.api_key;
      if (!apiKey) {
        useAlert('Сначала введите ключ модели');
        return;
      }
      this.aiVerifying = true;
      try {
        const { data } = await vibevoxLlmApi.verifyModels(apiKey);
        if (data?.ok && Array.isArray(data.models) && data.models.length) {
          this.aiModels = data.models;
          useAlert(`Ключ принят. Доступно моделей: ${data.models.length}`);
        } else {
          this.aiModels = [];
          useAlert(
            data?.error
              ? `Ошибка: ${data.error}`
              : 'Модели не найдены для этого ключа'
          );
        }
      } catch (error) {
        this.aiModels = [];
        const msg =
          error?.response?.data?.error ||
          error?.message ||
          'Не удалось проверить ключ';
        useAlert(`Ошибка проверки ключа: ${msg}`);
      } finally {
        this.aiVerifying = false;
      }
    },
  },
};
</script>

<template>
  <div class="flex flex-col h-auto overflow-auto integration-hooks">
    <woot-modal-header
      :header-title="integration.name"
      :header-content="replaceInstallationName(integration.short_description)"
    />
    <FormKit
      v-model="values"
      type="form"
      form-class="w-full grid gap-4"
      :submit-attrs="{
        inputClass: 'hidden',
        wrapperClass: 'hidden',
      }"
      :incomplete-message="false"
      @submit="submitForm"
    >
      <FormKit v-for="item in formItems" :key="item.name" v-bind="item" />
      <!-- VibeVox AI: проверка ключа модели и выбор модели из доступных по ключу -->
      <template v-if="isIntegrationOpenai">
        <div class="flex items-center gap-3">
          <NextButton
            type="button"
            label="Проверить ключ"
            :is-loading="aiVerifying"
            @click.prevent="verifyAiKey"
          />
          <span class="text-n-slate-11 text-sm">
            {{
              aiModels.length
                ? `Доступно моделей: ${aiModels.length}`
                : 'Введите ключ и нажмите «Проверить»'
            }}
          </span>
        </div>
        <FormKit
          v-if="aiModels.length"
          type="select"
          name="model"
          label="Модель"
          :options="aiModels"
          placeholder="Выберите модель"
        />
        <!-- VibeVox: аккаунт-дефолт двустороннего перевода (на ВЕСЬ аккаунт) -->
        <FormKit
          type="checkbox"
          name="auto_translate"
          label="Авто-перевод диалогов по умолчанию (весь аккаунт)"
        />
        <FormKit
          type="select"
          name="op_lang"
          label="Язык оператора — на него переводить входящие"
          :options="langOptions"
          placeholder="Выберите язык"
        />
      </template>
      <FormKit
        v-if="isHookTypeInbox"
        :options="inboxes"
        type="select"
        name="inbox"
        input-class="reset-base"
        :placeholder="$t('INTEGRATION_APPS.ADD.FORM.INBOX.LABEL')"
        :label="$t('INTEGRATION_APPS.ADD.FORM.INBOX.PLACEHOLDER')"
        validation="required"
        validation-name="Inbox"
      />
      <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
        <NextButton
          faded
          slate
          type="reset"
          :label="$t('INTEGRATION_APPS.ADD.FORM.CANCEL')"
          @click.prevent="onClose"
        />
        <NextButton
          type="submit"
          :label="$t('INTEGRATION_APPS.ADD.FORM.SUBMIT')"
          :is-loading="uiFlags.isCreatingHook"
        />
      </div>
    </FormKit>
  </div>
</template>

<style lang="css">
.formkit-outer {
  @apply mt-2;
}

.formkit-form > .formkit-wrapper > ul.formkit-messages {
  @apply hidden;
}

.formkit-form .formkit-help {
  @apply text-n-slate-10 text-sm font-normal mt-2 w-full;
}

/* equivalent of .reset-base */
.formkit-input {
  margin-bottom: 0px !important;
}

[data-invalid] .formkit-message {
  @apply text-n-ruby-9 block text-xs font-normal my-1 w-full;
}

.formkit-outer[data-type='checkbox'] .formkit-wrapper {
  @apply flex items-center gap-2 px-0.5;
}

.formkit-messages {
  @apply list-none m-0 p-0;
}

.formkit-actions {
  @apply hidden;
}
</style>
